import { FormEvent, useState } from 'react';
import { AdminRole, AdminScreen, AdminScreenKey, SaveAdminRoleRequest } from '../../types/admin-roles';
import ScreenPicker from './ScreenPicker';

interface RoleFormDialogProps {
  screens: AdminScreen[];
  /** Existing role when editing; null when creating. */
  role: AdminRole | null;
  onSave: (body: SaveAdminRoleRequest) => Promise<void>;
  onClose: () => void;
}

export default function RoleFormDialog({ screens, role, onSave, onClose }: RoleFormDialogProps) {
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<AdminScreenKey[]>(role?.screenKeys ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (key: AdminScreenKey) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave({ name: name.trim(), description: description.trim() || null, screenKeys: selected });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal admin-modal-wide" onSubmit={submit}>
        <div className="admin-modal-header">
          <h3>{role ? `Edit ${role.name}` : 'Create role'}</h3>
        </div>

        <div className="admin-modal-body">
          <label className="admin-field">
            <span className="admin-field-label">Role name</span>
            <input
              className="admin-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={64}
              required
              autoFocus
            />
          </label>

          <label className="admin-field">
            <span className="admin-field-label">Description</span>
            <input
              className="admin-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={255}
              placeholder="What this role is for"
            />
          </label>

          <div className="admin-field">
            <span className="admin-field-label">Screens</span>
            <ScreenPicker screens={screens} selected={selected} onToggle={toggle} disabled={saving} />
            {selected.length === 0 && (
              <p className="admin-text-muted">
                This role grants no screens yet. Anyone holding only this role will see an empty portal.
              </p>
            )}
          </div>

          {error && <div className="admin-error">{error}</div>}
        </div>

        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={saving || name.trim().length === 0}
          >
            {saving ? 'Saving…' : role ? 'Save changes' : 'Create role'}
          </button>
        </div>
      </form>
    </div>
  );
}
